import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../store/store";
import { selectClusterList } from "../../../store/slices/clusterListSlice";
import { selectCurrentUser } from "../../../store/slices/currentUserSlice";
import { getApi } from "../../../api/api";
import {
  CartItem,
  PublicStoreInterface,
  SaleItemInterface,
} from "../../../common/types/interfaces";

export const useCartData = (clusterId: number) => {
  const api = getApi();
  const currentUser = useSelector(selectCurrentUser);
  const clusters = useSelector(selectClusterList);

  const cart = useSelector((state: RootState) => state.cart);

  const currentCluster = clusters.find((c) => Number(c.id) === clusterId);

  const cartItems = useMemo(
    () => cart.items.filter((item) => item.clusterId === clusterId),
    [cart.items, clusterId],
  );

  const [saleItemList, setSaleItemList] = useState<SaleItemInterface[]>([]);
  const [storeList, setStoreList] = useState<PublicStoreInterface[]>([]);
  const [ready, setReady] = useState(false);

  const itemIds = useMemo(
    () => Array.from(new Set(cartItems.map((i) => i.saleItemId))),
    [cartItems],
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setReady(false);

        const [items, stores] = await Promise.all([
          itemIds.length > 0
            ? api.getItems({ clusterId, ids: itemIds, limit: itemIds.length })
            : Promise.resolve({ data: [], total: 0 }),
          api.getStores({ clusterId, target: "logmall" }),
        ]);

        if (cancelled) return;

        setSaleItemList(items.data || []);
        setStoreList(stores || []);
      } catch {
        if (!cancelled) {
          setSaleItemList([]);
          setStoreList([]);
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [api, clusterId, itemIds]);

  return {
    currentCluster,
    currentUser,
    cart: { ...cart, items: cartItems },
    saleItemList,
    storeList,
    ready,
  };
};
